import { useCallback, useEffect, useState } from 'react';
import { useTranslation, Trans } from 'react-i18next';
import { getLocalFolderStatus, triggerLocalFolderScan } from '../api/cvs';
import type { LocalFolderStatus as LocalFolderStatusType } from '../types';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatDuration(ms: number): string {
  if (ms < 1000) return `${ms} ms`;
  return `${(ms / 1000).toFixed(1)} s`;
}

// ---------------------------------------------------------------------------
// Stat pill sub-component
// ---------------------------------------------------------------------------

interface StatPillProps {
  label: string;
  value: number;
  colorClass: string;
  testId?: string;
}

function StatPill({ label, value, colorClass, testId }: StatPillProps) {
  return (
    <div className={`flex flex-col items-center rounded-lg px-4 py-3 ${colorClass}`} data-testid={testId}>
      <span className="text-2xl font-bold leading-none" aria-label={`${value} ${label}`}>
        {value}
      </span>
      <span className="mt-1 text-xs font-medium uppercase tracking-wide opacity-80">
        {label}
      </span>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export default function LocalFolderStatus() {
  const { t } = useTranslation();
  const [status, setStatus] = useState<LocalFolderStatusType | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [scanMessage, setScanMessage] = useState<string | null>(null);
  const [scanError, setScanError] = useState<string | null>(null);

  /**
   * Return a human-readable relative time string using i18n.
   * Falls back to a locale-formatted date for times older than 24 hours.
   */
  function relativeTime(isoString: string): string {
    const diffMs = Date.now() - new Date(isoString).getTime();
    const diffSec = Math.floor(diffMs / 1000);

    if (diffSec < 60) return t('localFolder.justNow');
    if (diffSec < 3600) {
      const mins = Math.floor(diffSec / 60);
      return t('localFolder.minutesAgo', { count: mins });
    }
    if (diffSec < 86400) {
      const hours = Math.floor(diffSec / 3600);
      return t('localFolder.hoursAgo', { count: hours });
    }
    return new Date(isoString).toLocaleDateString(undefined, {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  }

  const loadStatus = useCallback(async () => {
    setRefreshing(true);
    setLoadError(null);
    try {
      const data = await getLocalFolderStatus();
      setStatus(data);
    } catch (err) {
      setLoadError(err instanceof Error ? err.message : t('localFolder.loadFailed'));
    } finally {
      setRefreshing(false);
    }
  }, [t]);

  useEffect(() => {
    loadStatus();
  }, [loadStatus]);

  async function handleScanNow() {
    setScanning(true);
    setScanMessage(null);
    setScanError(null);
    try {
      await triggerLocalFolderScan();
      setScanMessage(t('localFolder.scanTriggered'));
      // Give the backend a moment, then refresh the displayed status.
      await new Promise((resolve) => setTimeout(resolve, 1500));
      await loadStatus();
      setScanMessage(t('localFolder.scanComplete'));
    } catch (err) {
      setScanError(err instanceof Error ? err.message : t('localFolder.scanFailed'));
    } finally {
      setScanning(false);
    }
  }

  // -------------------------------------------------------------------------
  // Render states
  // -------------------------------------------------------------------------

  return (
    <section aria-label={t('localFolder.sectionAria')} className="card space-y-5">
      {/* Header row */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          {status !== null && (
            <span
              className={[
                'inline-block h-3 w-3 rounded-full flex-shrink-0',
                status.enabled ? 'bg-green-500' : 'bg-gray-300',
              ].join(' ')}
              aria-hidden="true"
            />
          )}
          <h2 className="text-base font-semibold text-gray-900">{t('localFolder.title')}</h2>
          {status !== null && (
            <span
              className={[
                'inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium',
                status.enabled
                  ? 'bg-green-50 text-green-700'
                  : 'bg-gray-100 text-gray-500',
              ].join(' ')}
              aria-label={t('localFolder.statusLabel', { status: status.enabled ? t('localFolder.enabled').toLowerCase() : t('localFolder.disabled').toLowerCase() })}
            >
              {status.enabled ? t('localFolder.enabled') : t('localFolder.disabled')}
            </span>
          )}
        </div>

        {/* Action buttons */}
        <div className="flex gap-2">
          <button
            type="button"
            onClick={loadStatus}
            disabled={refreshing || scanning}
            aria-busy={refreshing}
            aria-label={t('localFolder.refreshAria')}
            className="btn-secondary"
          >
            {refreshing ? t('localFolder.refreshing') : t('common.refresh')}
          </button>
          {status?.enabled && (
            <button
              type="button"
              onClick={handleScanNow}
              disabled={scanning || refreshing}
              aria-busy={scanning}
              aria-label={t('localFolder.scanAria')}
              className="btn-primary"
            >
              {scanning ? t('localFolder.scanning') : t('localFolder.scanNow')}
            </button>
          )}
        </div>
      </div>

      {/* Load error */}
      {loadError && (
        <div role="alert" className="rounded-lg bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700">
          {loadError}
        </div>
      )}

      {/* Scan feedback */}
      {scanMessage && !scanError && (
        <div role="status" aria-live="polite" className="rounded bg-green-50 border border-green-200 px-4 py-3 text-sm text-green-700">
          {scanMessage}
        </div>
      )}
      {scanError && (
        <div role="alert" className="rounded bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700">
          {scanError}
        </div>
      )}

      {/* Loading skeleton */}
      {status === null && !loadError && (
        <div role="status" aria-live="polite" className="flex items-center gap-3 text-gray-400 text-sm py-4">
          <span className="animate-spin text-brand-600" aria-hidden="true">⟳</span>
          {t('localFolder.loadingStatus')}
        </div>
      )}

      {/* Status details */}
      {status !== null && (
        <>
          {/* Folder path */}
          <dl className="grid sm:grid-cols-2 gap-x-8 gap-y-3 text-sm">
            <div>
              <dt className="font-medium text-gray-500">{t('localFolder.inboxPath')}</dt>
              <dd
                className="mt-0.5 font-mono text-xs text-gray-800 break-all"
                title={status.path}
              >
                {status.path || <span className="italic text-gray-400">{t('localFolder.notConfigured')}</span>}
              </dd>
            </div>
            <div>
              <dt className="font-medium text-gray-500">{t('localFolder.lastScan')}</dt>
              <dd className="mt-0.5 text-gray-800">
                {status.lastScanTime ? (
                  <time dateTime={status.lastScanTime}>
                    {relativeTime(status.lastScanTime)}
                    {status.lastScanDurationMs !== null && (
                      <span className="ml-2 text-xs text-gray-400">
                        ({formatDuration(status.lastScanDurationMs)})
                      </span>
                    )}
                  </time>
                ) : (
                  <span className="italic text-gray-400">{t('localFolder.never')}</span>
                )}
              </dd>
            </div>
          </dl>

          {/* File count pills */}
          <div className="flex flex-wrap gap-3" aria-label={t('localFolder.fileCounts')}>
            <StatPill
              label={t('localFolder.pending')}
              value={status.filesInInbox}
              colorClass="bg-amber-50 text-amber-800"
              testId="stat-inbox"
            />
            <StatPill
              label={t('localFolder.processed')}
              value={status.filesProcessed}
              colorClass="bg-green-50 text-green-800"
              testId="stat-processed"
            />
            <StatPill
              label={t('localFolder.failed')}
              value={status.filesFailed}
              colorClass={status.filesFailed > 0 ? 'bg-red-50 text-red-800' : 'bg-gray-50 text-gray-500'}
              testId="stat-failed"
            />
          </div>

          {/* Disabled notice */}
          {!status.enabled && (
            <div className="rounded-lg border border-gray-200 bg-gray-50 px-4 py-4 text-sm text-gray-600">
              <p className="font-medium text-gray-700">{t('localFolder.disabledTitle')}</p>
              <p className="mt-1">
                <Trans i18nKey="localFolder.disabledHint">
                  To enable automatic CV ingestion from a local folder, set{' '}
                  <code className="rounded bg-gray-200 px-1 py-0.5 font-mono text-xs">
                    LOCAL_FOLDER_ENABLED=true
                  </code>{' '}
                  and{' '}
                  <code className="rounded bg-gray-200 px-1 py-0.5 font-mono text-xs">
                    LOCAL_FOLDER_PATH=/path/to/inbox
                  </code>{' '}
                  in the backend environment configuration, then restart the service.
                </Trans>
              </p>
            </div>
          )}
        </>
      )}
    </section>
  );
}
